import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { millisecondsInDay, millisecondsInHour } from '@alltrails/data-formatting/dateConstants';
import useMap from '@alltrails/maps/hooks/useMap';
import type { ConditionStyleId } from '@alltrails/maps/types/Styles';
import { conditionsOverlaysBottomId, conditionsOverlaysTopId, getLayerBeforeId } from '@alltrails/maps/utils/layers';
import type { TrailId } from '@alltrails/types/TrailId';
import { useGetTrailWeatherOverviewQuery } from '../../redux/conditionsApi';
import { useAppSelector } from '../../redux/helpers';
import { getLocalizedDateStart, getUTCFromLocalized } from '../../utils/timeHelpers';
import ConditionOverlay from './ConditionOverlay';

const sourceId = 'condition-overlays-source';

const getConditionSpecificUTC = (condition: ConditionStyleId, utc: number) => {
  switch (condition) {
    case 'precipitation':
      return utc;
    case 'snowDepth':
    case 'temperature':
    default:
      // We fetch tiles at the previous whole hour for conditions other than precipitation, due to cost and performance server-side
      const newDate = new Date(utc);
      newDate.setUTCMinutes(0);
      newDate.setUTCSeconds(0);
      return newDate.getTime();
  }
};

type ConditionOverlaysProps = {
  trailId?: TrailId;
};

const ConditionOverlays = ({ trailId }: ConditionOverlaysProps) => {
  const map = useMap();

  const { activeCondition, displayMetric, preloadedOverlayUTCs, selectedUTC } = useAppSelector(state => ({
    activeCondition: state.conditions.activeCondition,
    displayMetric: state.context.displayMetric,
    preloadedOverlayUTCs: state.conditions.preloadedOverlayUTCs,
    selectedUTC: state.conditions.selectedUTC
  }));

  // Params should match TrailMapConditionsSidebarComponents use of the overview query to prevent unnecessary requests
  const { data: overviewData } = useGetTrailWeatherOverviewQuery(
    { sentenceUnits: displayMetric ? 'm' : 'i', trailId: trailId ?? -1 },
    { skip: !activeCondition || !trailId }
  );

  const maxZoom = useMemo(() => {
    switch (activeCondition) {
      case 'snowDepth':
        return 8;
      case 'precipitation':
      case 'temperature':
      default:
        return 5;
    }
  }, [activeCondition]);

  const overlayUTCs = useMemo(() => {
    if (!activeCondition || !selectedUTC) {
      return [];
    }

    // For snow depth, display the end of day snow depth overlay localized to the trail (ie. 11pm local time to trail)
    if (activeCondition === 'snowDepth') {
      const utcOffsetSeconds = overviewData?.secondsFromUtc;
      const localDateStart = getLocalizedDateStart(selectedUTC, utcOffsetSeconds ?? 0);
      const local11pm = localDateStart + millisecondsInDay - millisecondsInHour;
      const utc11pm = getUTCFromLocalized(local11pm, utcOffsetSeconds ?? 0);
      return [utc11pm];
    }

    let utcs = preloadedOverlayUTCs;
    if (!preloadedOverlayUTCs.includes(selectedUTC)) {
      utcs = [...preloadedOverlayUTCs, selectedUTC];
    }

    // Use a Set because different utcs can lead to duplicate overlays depending on the activeCondition, based on the logic in getConditionSpecificUTC
    return Array.from(new Set(utcs.map(utc => getConditionSpecificUTC(activeCondition, utc))));
  }, [activeCondition, preloadedOverlayUTCs, overviewData?.secondsFromUtc, selectedUTC]);

  if (!activeCondition || activeCondition === 'groundConditions' || !selectedUTC) {
    return null;
  }

  return (
    <>
      <Source id={sourceId} type="geojson">
        <Layer id={conditionsOverlaysTopId} beforeId={getLayerBeforeId(map, conditionsOverlaysTopId)} type="symbol" source={sourceId} />
        <Layer id={conditionsOverlaysBottomId} beforeId={conditionsOverlaysTopId} type="symbol" source={sourceId} />
      </Source>
      {overlayUTCs.map(utc => {
        const isActive = activeCondition === 'snowDepth' || utc === getConditionSpecificUTC(activeCondition, selectedUTC);
        return (
          <ConditionOverlay key={`${activeCondition}-${utc}`} activeCondition={activeCondition} isActive={isActive} maxZoom={maxZoom} utc={utc} />
        );
      })}
    </>
  );
};

export default ConditionOverlays;
